import React from 'react';
import { HeroSection } from '../components/HeroSection';
import { TimelineItem } from '../components/TimelineItem';
import { BoundaryCard } from '../components/BoundaryCard';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
export function FatLiberation() {
  return <div className="min-h-screen bg-brand-cream" data-id="element-1186">
      <HeroSection title="What Fat Liberation Means (To Us)" tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-1187" />

      {/* Definition Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1188">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1189">
          <div data-id="element-1190">
            <h2 className="text-4xl font-black uppercase mb-8 text-brand-black" data-id="element-1191">
              Our Definition
            </h2>
            <div className="prose prose-lg text-gray-700" data-id="element-1192">
              <p className="mb-6" data-id="element-1193">
                <strong className="text-brand-magenta" data-id="element-1194">Fat liberation</strong>{' '}
                is a political movement demanding the complete dismantling of
                systems, policies, and cultural norms that oppress, marginalize,
                and kill fat people.
              </p>
              <p className="mb-6" data-id="element-1195">
                It centers those most harmed by anti-fat bias—particularly fat
                Black, Indigenous, disabled, trans, and poor people—and refuses
                to separate fatness from other forms of systemic oppression.
              </p>
              <p data-id="element-1196">
                Fat liberation is not about individual self-acceptance. It's
                about collective resistance and structural change.
              </p>
            </div>
          </div>

          <div className="bg-brand-navy p-8 md:p-10 text-white rounded-sm shadow-xl" data-id="element-1197">
            <h3 className="text-2xl font-black uppercase mb-8 border-b border-gray-700 pb-4" data-id="element-1198">
              What We're Not
            </h3>

            <div className="space-y-6" data-id="element-1199">
              <div data-id="element-1200">
                <h4 className="text-brand-magenta font-bold text-lg mb-2" data-id="element-1201">
                  Not Body Positivity
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-1202">
                  Body positivity has been co-opted by thin people and
                  corporations. It focuses on feeling good about yourself while
                  systems remain unchanged.
                </p>
              </div>

              <div data-id="element-1203">
                <h4 className="text-brand-cyan font-bold text-lg mb-2" data-id="element-1204">
                  Not Self-Love
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-1205">
                  You don't have to love your body to deserve human rights,
                  healthcare, and dignity.
                </p>
              </div>

              <div data-id="element-1206">
                <h4 className="text-brand-lime font-bold text-lg mb-2" data-id="element-1207">
                  Not Wellness
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-1208">
                  We reject healthism—the belief that health equals moral worth.
                  Fat people deserve full lives regardless of health status.
                </p>
              </div>

              <div data-id="element-1209">
                <h4 className="text-brand-orange font-bold text-lg mb-2" data-id="element-1210">
                  Not "All Bodies"
                </h4>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-1211">
                  This dilutes fat-specific oppression and centers those with
                  thin privilege.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lineage Section */}
      <section className="py-20 bg-white" data-id="element-1212">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1213">
          <h2 className="text-4xl font-black uppercase mb-16 text-brand-black border-b-4 border-brand-magenta inline-block pb-2" data-id="element-1214">
            Our Influences & Lineage
          </h2>

          <div className="space-y-4" data-id="element-1215">
            <TimelineItem era="1960s-70s" title="Fat Liberation Movement" description="The Fat Underground, Fat Acceptance Movement, and early radical organizing against diet culture and medical discrimination." meta="Key Figures: Judy Freespirit, Sara Fishman, Lynn McAfee" color="magenta" data-id="element-1216" />

            <TimelineItem era="1980s-90s" title="Disability Justice" description="Understanding fatness through the lens of disability politics, access, and the social model of disability." meta="Building on: Sins Invalid, ADAPT, Disability Rights Organizing" color="cyan" data-id="element-1217" />

            <TimelineItem era="2000s-Present" title="Intersectional Analysis" description="Centering race, class, gender, and colonialism in understanding anti-fat oppression. Naming how diet culture is rooted in anti-Blackness and white supremacy." meta="Key Works: Sabrina Strings, Da'Shaun Harrison, Sonya Renee Taylor" color="lime" data-id="element-1218" />

            <TimelineItem era="Contemporary Voices" title="Digital Praxis" description="Current writers, activists, and organizers building fat liberation praxis in digital and physical spaces." meta="Including: Aubrey Gordon, Your Fat Friend, Caleb Luna, Virgie Tovar" color="orange" data-id="element-1219" />
          </div>
        </div>
      </section>

      {/* Boundaries Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1220">
        <div className="mb-12" data-id="element-1221">
          <h2 className="text-4xl font-black uppercase mb-4 text-brand-black" data-id="element-1222">
            Our Boundaries
          </h2>
          <p className="text-xl text-gray-600" data-id="element-1223">
            What we won't tolerate or platform
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1224">
          <BoundaryCard title="No Diet Talk" description="This includes 'wellness' language, before/after photos, 'health journeys,' intentional weight loss, or any discussion of food as morally good or bad." color="magenta" data-id="element-1225" />
          <BoundaryCard title="No Healthism" description="We reject the idea that health determines worth. Fat people deserve dignity whether 'healthy' or not." color="cyan" data-id="element-1226" />
          <BoundaryCard title="No Thin Centering" description="This space centers fat people. Thin people are welcome as accomplices, not to share their own 'body image struggles.'" color="lime" data-id="element-1227" />
          <BoundaryCard title="No Respectability Politics" description="We won't argue that fat people 'deserve rights because we're healthy/active/trying.' Rights aren't conditional." color="orange" data-id="element-1228" />
          <BoundaryCard title="No Corporate Co-option" description="We refuse partnerships with diet companies, weight loss apps, or brands that profit from anti-fatness." color="magenta" // Repeating colors for visual balance
        data-id="element-1229" />
          <BoundaryCard title="No Single-Issue Politics" description="Fat liberation is bound up with all liberation struggles. We won't separate it from anti-racism, disability justice, or trans liberation." color="magenta" data-id="element-1230" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-brand-magenta text-white text-center px-4" data-id="element-1231">
        <div className="max-w-4xl mx-auto" data-id="element-1232">
          <h2 className="text-4xl md:text-6xl font-black uppercase mb-8 leading-tight" data-id="element-1233">
            If this resonates, you're in the right place
          </h2>
          <p className="text-xl md:text-2xl mb-12 opacity-90 font-medium" data-id="element-1234">
            This is the framework that guides everything we do at ReGrowth—from
            the courses we build to the content we publish.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-1235">
            <Button color="white" size="lg" data-id="element-1236">
              Explore Learning
            </Button>
            <Button variant="outline" color="white" size="lg" data-id="element-1237">
              Read The Publication
            </Button>
          </div>
        </div>
      </section>
    </div>;
}